import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

/** Material UI Packages. */
import { Box, Typography, createTheme, ThemeProvider, Button } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SaveIcon from '@mui/icons-material/Save';

/** HTTP Client Library. */
import axios from 'axios';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import useApiRequest from '../../../useApiRequest';
import { CustomExportToolbar } from '../../../CustomExport';
import CustomSnackbar from '../../../CustomSnackbar';
import { CustomSkeletonVer2 } from '../../../CustomSkeleton';
import { loadColumns, emptyColumns } from './ScheduleTableColumns';

/**
 * Function to update the position of a row in an array of rows.
 *
 * @param {number} initialIndex - The initial index of the row to be moved.
 * @param {number} newIndex - The new index where the row should be moved to.
 * @param {Array} rows - The array of rows.
 * @returns {Promise<Array>} A promise that resolves to the new array of rows with the row moved to the new position.
 */
const updateRowPosition = (initialIndex, newIndex, rows) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            const rowsClone = [...rows];
            const row = rowsClone.splice(initialIndex, 1)[0];
            rowsClone.splice(newIndex, 0, row);
            resolve(rowsClone);
        }, Math.random() * 500 + 100); // Random delay between 100 and 600 milliseconds
    });
};

/** Set up general theme. */
const theme = createTheme({
    palette: {
        primary: { light: '#80b2bf', main: '#00647e', dark: '#003a52' },
    },
    typography: {
        fontFamily: 'NotoSans',
        fontWeightRegular: 400,
        fontWeightBold: 600,
        fontSize: 14,
    },
});

/** Main component for the schedule table. */
export default function ScheduleTable(props) {
    const { userId } = props;
    /** Call custom context provider to determine the user legitimacy */
    const { getAccessTokenSilently } = useAuth0();
    /** Custom API Request function */
    const apiRequest = useApiRequest();

    const buttonRef = useRef(null);

    /** State for managing the snackbar that displays the result of an API request. */
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    /** State for datagrid row.  */
    const [loadRows, setLoadRows] = useState([]);
    const [emptyRows, setEmptyRows] = useState([]);

    const [loadOrder, setLoadOrder] = useState([]);
    const [emptyOrder, setEmptyOrder] = useState([]);
    /** State to display loading component until API response is received. */
    const [loaded, setLoaded] = useState(false);
    /** State to manage the disabled state of the button. */
    const [disableBtn, setDisableBtn] = useState(true);

    /** Set document title. */
    useEffect(() => {
        document.title = 'Current D/O List | Dispatch System ';
    }, []);

    /** Async API call to obtain current D/O list and order data. */
    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const token = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: 'https://insightshippinginc.us.auth0.com/api/v2/',
                    },
                });
                const response = await axios.get('https://icxhzxkwxa.execute-api.us-west-1.amazonaws.com/api', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { type: 'overview', userId: userId },
                });
                const resp = response.data['body-json'].body;
                const load = resp.dispatch.filter((item) => item.isLoad);
                const empty = resp.dispatch.filter((item) => !item.isLoad);
                const loadOrder = resp.row_order.filter((item) => item.isLoad);

                const emptyOrder = resp.row_order.filter((item) => !item.isLoad);
                setLoadRows(load);
                setEmptyRows(empty);
                setLoadOrder(loadOrder);
                setEmptyOrder(emptyOrder);

                setLoaded(true);
            } catch (e) {}
        })();
    }, [getAccessTokenSilently, userId]);

    useEffect(() => {
        const sortAndSetRows = (order, rows, setRows) => {
            if (!order) return;

            const orderMap = new Map(order.map((item) => [item.do_id, item.row_id]));
            const sorted = [...rows].sort((a, b) => {
                const orderA = orderMap.get(a.id) ?? Infinity;
                const orderB = orderMap.get(b.id) ?? Infinity;
                return orderA - orderB;
            });
            setRows(sorted);
        };

        sortAndSetRows(loadOrder, loadRows, setLoadRows);
        sortAndSetRows(emptyOrder, emptyRows, setEmptyRows);
    }, [loadOrder, emptyOrder]);

    /** Function to handle key press events for save button. */
    useEffect(() => {
        const handleKeyPress = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault();
                buttonRef.current.click();
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [buttonRef]);

    /** Asynchronously update the position of rows. */
    const handleRowOrderChange = async (params) => {
        const isLoad = params.row.isLoad;
        if (isLoad) {
            const newRows = await updateRowPosition(params.oldIndex, params.targetIndex, loadRows);
            setLoadRows(newRows);
        } else {
            const newRows = await updateRowPosition(params.oldIndex, params.targetIndex, emptyRows);
            setEmptyRows(newRows);
        }
        setDisableBtn(false);
    };

    /** Handle to save row ordering. */
    const handleSave = () => {
        const loadOrderingData = [...loadRows].map((item, index) => ({ row_id: index + 1, do_id: item.id }));
        const emptyOrderingData = [...emptyRows].map((item, index) => ({ row_id: index + 1, do_id: item.id }));
        setDisableBtn(true);
        apiRequest('overview', 'order_by', userId, { load_order: loadOrderingData, empty_order: emptyOrderingData })
            .then((response) => {
                if (response.statusCode === 200) {
                    setSnackbar({ open: true, message: response.body.message, severity: 'success' });
                } else {
                    setSnackbar({ open: true, message: response.body.message, severity: 'error' });
                }
            })
            .catch((error) => {
                setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
            });
    };

    /** Refresh schedule data on button click. */
    const handleRefresh = () => {
        setLoaded(false);
        apiRequest('overview', 'refresh', userId, [])
            .then((response) => {
                if (response.statusCode === 200) {
                    const resp = response.body;
                    const load = resp.dispatch.filter((item) => item.isLoad);
                    const empty = resp.dispatch.filter((item) => !item.isLoad);
                    const loadOrder = resp.row_order.filter((item) => item.isLoad);
                    const emptyOrder = resp.row_order.filter((item) => !item.isLoad);
                    setLoadRows(load);
                    setEmptyRows(empty);
                    setLoadOrder(loadOrder);
                    setEmptyOrder(emptyOrder);
                    setDisableBtn(true);
                    setSnackbar({ open: true, message: 'Successful', severity: 'success' });
                    setLoaded(true);
                } else {
                    setSnackbar({ open: true, message: response.body.message, severity: 'error' });
                }
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
            });
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ p: 2 }}>
                {loaded ? (
                    <>
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '1.3rem',
                                    fontWeight: 'bold',
                                    color: '#003a52',
                                    display: 'inline',
                                    mr: 2,
                                }}
                            >
                                Current D/O (Load & Empty)
                            </Typography>
                            <Button
                                sx={{
                                    fontSize: 12,
                                    color: '#00647e',
                                    fontWeight: 'bold',
                                    borderRadius: 5,
                                    border: '1px solid #00647e',
                                    py: 0.3,
                                    px: 1,
                                    ml: 1,
                                }}
                                startIcon={<AutorenewIcon size="small" />}
                                onClick={handleRefresh}
                            >
                                Refresh
                            </Button>
                            <Button
                                ref={buttonRef}
                                sx={{
                                    fontSize: 12,
                                    color: 'white',
                                    backgroundColor: disableBtn ? '#ced4da' : '#003a52',
                                    fontWeight: 'bold',
                                    borderRadius: 5,
                                    border: disableBtn ? '1px solid grey' : '1px solid #003a52',
                                    py: 0.3,
                                    px: 1,
                                    ml: 1,
                                }}
                                startIcon={<SaveIcon fontSize="small" />}
                                onClick={handleSave}
                                disabled={disableBtn}
                            >
                                Save
                            </Button>
                        </Box>
                        {['load', 'empty'].map((item, idx) => {
                            return (
                                <Box key={idx} sx={{ width: '100%', height: '45vh' }}>
                                    <DataGridPremium
                                        rows={item == 'load' ? loadRows : emptyRows}
                                        columns={item == 'load' ? loadColumns : emptyColumns}
                                        density="compact"
                                        rowHeight={35}
                                        columnHeaderHeight={35}
                                        disableRowSelectionOnClick
                                        disalbec
                                        slots={{ toolbar: CustomExportToolbar }}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: true,
                                            },
                                        }}
                                        rowReordering
                                        onRowOrderChange={handleRowOrderChange}
                                        sx={{
                                            fontSize: 12,
                                            '& .MuiDataGrid-columnHeader': {
                                                maxHeight: '26px ',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                backgroundColor:
                                                    item == 'load'
                                                        ? 'rgba(217, 237, 146,0.5)'
                                                        : 'rgba(184, 192, 255,0.5)',
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </>
                ) : (
                    <CustomSkeletonVer2 />
                )}
                <CustomSnackbar snackbar={snackbar} setSnackbar={setSnackbar} />
            </Box>
        </ThemeProvider>
    );
}
